import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Plus, Users, Trash2, User, Search, Building2, RefreshCw, Clock, Mail, MailX, AlertTriangle, CheckCircle, Shield, UserX, ArrowRightLeft, KeyRound } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PiktableLoader } from "@/components/PiktableLoader";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

interface UserWithEstablishment {
  id: string;
  email: string;
  created_at: string;
  email_confirmed_at: string | null;
  last_sign_in_at: string | null;
  role: 'admin' | 'staff' | null;
  establishment_id: string | null;
  establishment_name: string | null;
  establishment_active: boolean | null;
  is_super_admin?: boolean;
}

interface Establishment {
  id: string;
  name: string;
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { isSuperAdmin, loading: authLoading } = useAuth();
  const [users, setUsers] = useState<UserWithEstablishment[]>([]);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithEstablishment | null>(null);
  
  // Filters - initialize from URL params
  const [searchQuery, setSearchQuery] = useState("");
  const [filterEstablishment, setFilterEstablishment] = useState<string>(() => {
    const urlEstablishment = searchParams.get('establishment');
    return urlEstablishment || "all";
  });
  const [filterRole, setFilterRole] = useState<string>("all");
  const [filterEmailStatus, setFilterEmailStatus] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("email");
  
  // Form state
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<'admin' | 'staff' | 'super-admin'>('staff');
  const [selectedEstablishment, setSelectedEstablishment] = useState<string>("");

  useEffect(() => {
    // Wait for auth to finish loading before checking super admin status
    if (authLoading) return;
    
    if (!isSuperAdmin) {
      toast({
        title: "Accès refusé",
        description: "Seuls les super-admins peuvent accéder à cette page.",
        variant: "destructive",
      });
      navigate('/dashboard');
    }
  }, [isSuperAdmin, authLoading, navigate, toast]);

  const fetchEstablishments = async () => {
    try {
      const { data, error } = await supabase
        .from('establishments')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setEstablishments(data || []);
    } catch (error) {
      console.error('Error fetching establishments:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('manage-users', {
        headers: {
          Authorization: `Bearer ${currentSession?.access_token}`
        },
        body: { action: 'list' }
      });

      if (error) throw error;
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les utilisateurs.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchEstablishments();
      fetchUsers();
    }
  }, [isSuperAdmin]);

  const handleCreateUser = async () => {
    const isSuperAdminRole = role === 'super-admin';
    if (!email || (!isSuperAdminRole && !selectedEstablishment)) {
      toast({
        title: "Erreur",
        description: isSuperAdminRole ? "Email requis." : "Email et établissement requis.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const body: Record<string, unknown> = {
        action: 'create',
        email,
        role,
      };
      if (!isSuperAdminRole) {
        body.establishmentId = selectedEstablishment;
      }
      const { data, error } = await supabase.functions.invoke('manage-users', {
        headers: {
          Authorization: `Bearer ${currentSession?.access_token}`
        },
        body
      });

      if (error) {
        let errorMessage = "Impossible de créer l'utilisateur.";
        try {
          const errorBody = typeof error.context?.body === 'string' 
            ? JSON.parse(error.context.body) 
            : error.context?.body;
          if (errorBody?.error) {
            errorMessage = errorBody.error;
          }
        } catch {
          // Use default message
        }
        
        toast({
          title: "Erreur",
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Utilisateur créé",
        description: `${email} a été créé. Un email de configuration a été envoyé.`,
      });

      setIsDialogOpen(false);
      setEmail("");
      setRole('staff');
      setSelectedEstablishment("");
      await fetchUsers();
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast({
        title: "Erreur",
        description: "Impossible de créer l'utilisateur.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateRole = async (userId: string, newRole: 'admin' | 'staff', establishmentId: string | null) => {
    if (!establishmentId) return;
    
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const { error } = await supabase.functions.invoke('manage-users', {
        headers: {
          Authorization: `Bearer ${currentSession?.access_token}`
        },
        body: {
          action: 'update-role',
          userId,
          role: newRole,
          establishmentId
        }
      });

      if (error) throw error;

      toast({
        title: "Rôle modifié",
        description: "Le rôle de l'utilisateur a été mis à jour.",
      });

      await fetchUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier le rôle.",
        variant: "destructive",
      });
    }
  };

  const handleAssignEstablishment = async () => {
    if (!selectedUser || !selectedEstablishment) {
      toast({
        title: "Erreur",
        description: "Établissement requis.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const { error } = await supabase.functions.invoke('manage-users', {
        headers: {
          Authorization: `Bearer ${currentSession?.access_token}`
        },
        body: {
          action: 'assign-establishment',
          userId: selectedUser.id,
          role,
          establishmentId: selectedEstablishment
        }
      });

      if (error) throw error;

      toast({
        title: "Établissement assigné",
        description: `${selectedUser.email} a été assigné à l'établissement.`,
      });

      setIsAssignDialogOpen(false);
      setSelectedUser(null);
      setRole('staff');
      setSelectedEstablishment("");
      await fetchUsers();
    } catch (error) {
      console.error('Error assigning establishment:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'assigner l'établissement.",
        variant: "destructive",
      });
    }
  };

  const handleSendPasswordReset = async (userEmail: string) => {
    if (!confirm(`Envoyer un email de réinitialisation de mot de passe à ${userEmail} ?`)) return;

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('request-password-reset', {
        headers: {
          Authorization: `Bearer ${currentSession?.access_token}`,
        },
        body: {
          email: userEmail,
          slug: null,
          triggered_by_admin: true,
        },
      });

      if (error) throw error;

      toast({
        title: "Email envoyé",
        description: `Un lien de réinitialisation a été envoyé à ${userEmail}.${data?.resend_id ? ` (id: ${data.resend_id})` : ""}`,
      });
    } catch (error) {
      console.error('Error sending password reset:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer l'email de réinitialisation.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteUser = async (userId: string, establishmentId: string | null) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) return;

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const { error } = await supabase.functions.invoke('manage-users', {
        headers: {
          Authorization: `Bearer ${currentSession?.access_token}`
        },
        body: {
          action: 'delete',
          userId,
          establishmentId
        }
      });

      if (error) throw error;

      toast({
        title: "Utilisateur supprimé",
        description: "L'utilisateur a été supprimé avec succès.",
      });

      await fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'utilisateur.",
        variant: "destructive",
      });
    }
  };

  const handleToggleSuperAdmin = async (userId: string, currentIsSuperAdmin: boolean) => {
    const action = currentIsSuperAdmin ? "retirer le statut super-admin de" : "promouvoir en super-admin";
    if (!confirm(`Êtes-vous sûr de vouloir ${action} cet utilisateur ?`)) return;

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('manage-users', {
        headers: {
          Authorization: `Bearer ${currentSession?.access_token}`
        },
        body: {
          action: 'toggle-super-admin',
          userId
        }
      });

      if (error) throw error;

      toast({
        title: data.is_super_admin ? "Super-admin ajouté" : "Super-admin retiré",
        description: data.is_super_admin 
          ? "L'utilisateur est maintenant super-admin."
          : "Le statut super-admin a été retiré.",
      });

      await fetchUsers();
    } catch (error: any) {
      console.error('Error toggling super admin:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de modifier le statut super-admin.",
        variant: "destructive",
      });
    }
  };

  const openAssignDialog = (user: UserWithEstablishment, preSelectCurrentEst = false) => {
    setSelectedUser(user);
    setRole(user.role || 'staff');
    setSelectedEstablishment(preSelectCurrentEst && user.establishment_id ? user.establishment_id : "");
    setIsAssignDialogOpen(true);
  };

  const handleRemoveFromEstablishment = async (userId: string, userEmail: string) => {
    if (!confirm(`Êtes-vous sûr de vouloir retirer ${userEmail} de son établissement ? L'utilisateur deviendra orphelin mais ne sera pas supprimé.`)) return;

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const { error } = await supabase.functions.invoke('manage-users', {
        headers: {
          Authorization: `Bearer ${currentSession?.access_token}`
        },
        body: {
          action: 'remove-from-establishment',
          userId
        }
      });

      if (error) throw error;

      toast({
        title: "Utilisateur retiré",
        description: `${userEmail} a été retiré de son établissement.`,
      });

      await fetchUsers();
    } catch (error) {
      console.error('Error removing from establishment:', error);
      toast({
        title: "Erreur",
        description: "Impossible de retirer l'utilisateur de l'établissement.",
        variant: "destructive",
      });
    }
  };

  // Filter users
  const filteredUsers = users
    .filter(user => {
      const matchesSearch = !searchQuery || 
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.establishment_name?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesEstablishment = 
        filterEstablishment === "all" || 
        (filterEstablishment === "none" && !user.establishment_id) ||
        user.establishment_id === filterEstablishment;
      
      const matchesRole = 
        filterRole === "all" || 
        (filterRole === "super_admin" && user.is_super_admin) ||
        (filterRole === "none" && !user.role && !user.is_super_admin) ||
        user.role === filterRole;

      const matchesEmailStatus = 
        filterEmailStatus === "all" ||
        (filterEmailStatus === "confirmed" && user.email_confirmed_at) ||
        (filterEmailStatus === "unconfirmed" && !user.email_confirmed_at);
      
      return matchesSearch && matchesEstablishment && matchesRole && matchesEmailStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "establishment":
          const estA = a.establishment_name || "zzz";
          const estB = b.establishment_name || "zzz";
          return estA.localeCompare(estB);
        case "role":
          const roleA = a.role || "zzz";
          const roleB = b.role || "zzz";
          return roleA.localeCompare(roleB);
        case "created":
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case "lastLogin":
          const loginA = a.last_sign_in_at ? new Date(a.last_sign_in_at).getTime() : 0;
          const loginB = b.last_sign_in_at ? new Date(b.last_sign_in_at).getTime() : 0;
          return loginB - loginA;
        default:
          return a.email.localeCompare(b.email);
      }
    });

  // Stats
  const totalUsers = users.length;
  const superAdminCount = users.filter(u => u.is_super_admin).length;
  const adminCount = users.filter(u => u.role === 'admin').length;
  const staffCount = users.filter(u => u.role === 'staff').length;
  const orphanCount = users.filter(u => !u.establishment_id && !u.is_super_admin).length;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatRelativeDate = (dateStr: string | null) => {
    if (!dateStr) return 'Jamais';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaines`;
    return formatDate(dateStr);
  };

  if (authLoading || !isSuperAdmin) {
    return <PiktableLoader message="Vérification des permissions..." />;
  }

  if (loading) {
    return <PiktableLoader message="Chargement des utilisateurs..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="container mx-auto p-6 max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Link to="/admin">
              <Button variant="ghost" size="sm" className="mb-2">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à l'administration
              </Button>
            </Link>
            <h1 className="text-3xl font-serif font-bold flex items-center gap-3">
              <Users className="w-8 h-8 text-primary" />
              Gestion des utilisateurs
            </h1>
            <p className="text-muted-foreground mt-1">
              {totalUsers} utilisateur{totalUsers > 1 ? 's' : ''} au total
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchUsers}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouvel utilisateur
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par email ou établissement..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterEstablishment} onValueChange={setFilterEstablishment}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="Établissement" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les établissements</SelectItem>
                  <SelectItem value="none">Sans établissement</SelectItem>
                  {establishments.map((est) => (
                    <SelectItem key={est.id} value={est.id}>{est.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger className="w-full md:w-[150px]">
                  <SelectValue placeholder="Rôle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les rôles</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="none">Sans rôle</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterEmailStatus} onValueChange={setFilterEmailStatus}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Statut email" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="confirmed">Email confirmé</SelectItem>
                  <SelectItem value="unconfirmed">Email non confirmé</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Trier par" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Trier par email</SelectItem>
                  <SelectItem value="establishment">Trier par établissement</SelectItem>
                  <SelectItem value="role">Trier par rôle</SelectItem>
                  <SelectItem value="created">Trier par date création</SelectItem>
                  <SelectItem value="lastLogin">Trier par dernière connexion</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total</CardTitle>
              <Users className="w-4 h-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{totalUsers}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Admins</CardTitle>
              <User className="w-4 h-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{adminCount}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Staff</CardTitle>
              <Users className="w-4 h-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{staffCount}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Sans établissement</CardTitle>
              <AlertTriangle className="w-4 h-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{orphanCount}</div>
            </CardContent>
          </Card>
        </div>

        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle>
              Utilisateurs ({filteredUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredUsers.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Aucun utilisateur trouvé</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex flex-col p-4 border rounded-lg hover:bg-muted/50 transition-colors gap-3"
                  >
                    {/* Top row: Email + badges */}
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold">{user.email}</p>
                      
                      {/* Email status badge */}
                      {user.email_confirmed_at ? (
                        <Badge variant="outline" className="text-green-600 border-green-600/30 bg-green-50 dark:bg-green-900/20">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Vérifié
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-orange-600 border-orange-600/30 bg-orange-50 dark:bg-orange-900/20">
                          <MailX className="w-3 h-3 mr-1" />
                          Non vérifié
                        </Badge>
                      )}

                      {/* No establishment badge */}
                      {!user.establishment_id && (
                        <Badge variant="outline" className="text-amber-600 border-amber-600/30 bg-amber-50 dark:bg-amber-900/20">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Sans établissement
                        </Badge>
                      )}

                      {/* Super Admin badge */}
                      {user.is_super_admin && (
                        <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                          <Shield className="w-3 h-3 mr-1" />
                          Super Admin
                        </Badge>
                      )}

                      {/* Role badge */}
                      {user.role ? (
                        <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                          {user.role === 'admin' ? 'Admin' : 'Staff'}
                        </Badge>
                      ) : !user.is_super_admin ? (
                        <Badge variant="outline" className="text-muted-foreground">
                          Sans rôle
                        </Badge>
                      ) : null}
                    </div>

                    {/* Info row */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      {/* Establishment */}
                      <div className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {user.establishment_name ? (
                          <span>
                            {user.establishment_name}
                            {user.establishment_active === false && (
                              <span className="text-red-500 ml-1">(inactif)</span>
                            )}
                          </span>
                        ) : (
                          <span className="italic">Aucun</span>
                        )}
                      </div>
                      
                      {/* Last sign in */}
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>Dernière connexion: {formatRelativeDate(user.last_sign_in_at)}</span>
                      </div>

                      {/* Created at */}
                      <div className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        <span>Créé le {formatDate(user.created_at)}</span>
                      </div>
                    </div>

                    {/* Actions row */}
                    <div className="flex items-center gap-3 flex-wrap">
                      {/* Super Admin toggle */}
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-md">
                        <Shield className="w-4 h-4 text-amber-500" />
                        <Label htmlFor={`super-admin-${user.id}`} className="text-sm cursor-pointer">
                          Super Admin
                        </Label>
                        <Switch
                          id={`super-admin-${user.id}`}
                          checked={user.is_super_admin || false}
                          onCheckedChange={() => handleToggleSuperAdmin(user.id, user.is_super_admin || false)}
                        />
                      </div>

                      {user.establishment_id ? (
                        <>
                          <Select
                            value={user.role || 'staff'}
                            onValueChange={(value: 'admin' | 'staff') => handleUpdateRole(user.id, value, user.establishment_id)}
                          >
                            <SelectTrigger className="w-28">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="staff">Staff</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openAssignDialog(user, false)}
                            title="Changer d'établissement"
                          >
                            <ArrowRightLeft className="w-4 h-4 mr-2" />
                            Changer
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleRemoveFromEstablishment(user.id, user.email)}
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                            title="Retirer de l'établissement"
                          >
                            <UserX className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openAssignDialog(user)}
                        >
                          <Building2 className="w-4 h-4 mr-2" />
                          Assigner un établissement
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendPasswordReset(user.email)}
                        title="Envoyer un email de réinitialisation de mot de passe"
                      >
                        <KeyRound className="w-4 h-4 mr-2" />
                        Reset MDP
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteUser(user.id, user.establishment_id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create User Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouvel utilisateur</DialogTitle>
              <DialogDescription>
                Créez un nouveau compte. L'utilisateur recevra un email pour configurer son mot de passe.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Rôle</Label>
                <Select value={role} onValueChange={(value: 'admin' | 'staff' | 'super-admin') => setRole(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="super-admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {role !== 'super-admin' ? (
                <div className="space-y-2">
                  <Label htmlFor="establishment">Établissement</Label>
                  <Select value={selectedEstablishment} onValueChange={setSelectedEstablishment}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un établissement" />
                    </SelectTrigger>
                    <SelectContent>
                      {establishments.map((est) => (
                        <SelectItem key={est.id} value={est.id}>{est.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Les super-admins ont accès à tous les établissements.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreateUser}>
                Créer l'utilisateur
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Establishment Dialog */}
        <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assigner un établissement</DialogTitle>
              <DialogDescription>
                Assigner {selectedUser?.email} à un établissement.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="assign-establishment">Établissement</Label>
                <Select value={selectedEstablishment} onValueChange={setSelectedEstablishment}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un établissement" />
                  </SelectTrigger>
                  <SelectContent>
                    {establishments.map((est) => (
                      <SelectItem key={est.id} value={est.id}>{est.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="assign-role">Rôle</Label>
                <Select value={role} onValueChange={(value: 'admin' | 'staff') => setRole(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleAssignEstablishment}>
                Assigner
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
